import {
  CloseOutlined,
  PauseOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Checkbox, Image, Progress, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import { useMenuContext } from "../../context/MenuContext";
import { useInfoUser } from "../../store/UserStore";
import { formatTime, formatTimeBySeconds } from "../../utils/formatTime";
import { getFileIcon } from "../../utils/SendFile";
import {
  isAudioFile,
  isImageFile,
  isSupportedFileType,
  isVideoFile,
  openNotificationError,
  standardizeVolume,
} from "../../utils/Utils";
import { useGlobalDownloader } from '../../context/global/DownloadProvider';
import { useImageLoader } from "../customHook/ImageLoader";
import MediaStorageModal from "../modal/MediaStorageModal";
import { PreviewOfficeModal } from "../modal/PreviewOfficeFile/PreviewOfficeModal";

const fileStore = process.env.REACT_APP_FILE_STORE || "http://10.1.1.230:8000";

const MailStorageFile = ({ file, pickFile, pickedList }) => {
  const { languageMap } = useInfoUser();
  const { selectedConversation } = useMenuContext() || {};

  const [resourceId, setResourceId] = useState(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [isOpenMediaModal, setIsOpenMediaModal] = useState(false);

  const [isFilePreviewModal, setIsFilePreviewModal] = useState(false);
  const [previewFileError, setPreviewFileError] = useState(false);

  const { imageUrl, loading, error } = useImageLoader(file?.resourceId);
  const { imageUrl: videoImgResourceUrl } = useImageLoader(
    file?.videoImgResourceId,
  );

  const fileRef = useRef(file);

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const [fileState, setFileState] = useState({});
  const fileNameToSave = file?.fileName || "Willo-video";
  const extensions = file?.originalName
    ?.substring(file?.originalName?.lastIndexOf(".") + 1)
    ?.toLowerCase();

  const handleResumeDownload = (e) => {
    e.stopPropagation();
    resumeDownload(file?.resourceId, fileNameToSave);
  };

  const handlePauseDownload = (e) => {
    e.stopPropagation();
    pauseDownload(file?.resourceId);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    startDownload(file?.resourceId, fileNameToSave, 0);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    cancelDownload(file?.resourceId);
  };

  const fileIcon = useMemo(() => {
    return getFileIcon(file?.fileName);
  }, [file?.fileName]);

  const openFilePreviewModal = () => {
    if (!isSupportedFileType(file?.fileName)) return;

    const pathUrl = file?.path;

    if (!pathUrl) {
      openNotificationError(
        languageMap?.["preview.error.pathNotFound"] ??
          "Error opening file due to non-existent path",
      );
      return;
    }

    setUrlPreview(
      `${fileStore}/conversation/${file?.conversationId}${pathUrl}`,
    );

    setResourceId(file?.resourceId);

    setIsFilePreviewModal(true);
  };

  const showPreview = (file) => {
    if (file?.videoImgResourceId) {
      return (
        <div>
          <Image src={videoImgResourceUrl} preview={false} />
          <div className="absolute inset-0 flex items-center justify-center">
            <FaPlayCircle size={40} color="white" />
          </div>
        </div>
      );
    }

    if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp", "jfif"]?.includes(
        file?.fileName
          ?.slice(file?.fileName?.lastIndexOf(".") + 1, file?.fileName?.length)
          ?.toLowerCase(),
      )
    ) {
      return <Image src={imageUrl || videoImgResourceUrl} preview={false} />;
    }

    switch (file?.contentType) {
      case "FILE": {
        if (file?.googleDriveFileId && !previewFileError) {
          const urlThumbnail = `https://images.weserv.nl/?url=drive.google.com/thumbnail?id=${file.googleDriveFileId}&width=300`;
          return (
            <Image
              src={urlThumbnail}
              preview={false}
              onError={() => setPreviewFileError(true)}
            />
          );
        }
        return <div>{fileIcon}</div>;
      }
      default: {
        return <div>{fileIcon}</div>;
      }
    }
  };

  const renderDownloadButton = useCallback(() => {
    switch (fileState?.status) {
      case DOWNLOAD_STATUS.PAUSED:
        return (
          <div className="flex gap-2">
            <button onClick={handleResumeDownload}>
              <PlayCircleOutlined
                style={{ fontSize: "20px", color: "green" }}
              />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      case DOWNLOAD_STATUS.DOWNLOADING:
        return (
          <div className="flex gap-2">
            <button onClick={handlePauseDownload}>
              <PauseOutlined className="text-[20px] text-red" />
            </button>
            <button onClick={handleCancel}>
              <CloseOutlined className="text-[20px] text-red" />
            </button>
          </div>
        );

      case DOWNLOAD_STATUS.SUCCESS:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload className="text-[20px] text-[#2b9853]" />
          </button>
        );
      case DOWNLOAD_STATUS.ERROR:
        return (
          <div className="flex gap-2">
            <button onClick={handleCancel}>
              <CloseOutlined style={{ fontSize: "20px", color: "red" }} />
            </button>
          </div>
        );
      default:
        return (
          <button onClick={handleDownload}>
            <IoMdDownload className="text-[20px] text-[#2b9853]" />
          </button>
        );
    }
  }, [fileState?.status]);

  const handleOpenMediaModal = () => {
    const isImage = isImageFile(file?.fileName) || false;
    const isAudio = isAudioFile(file?.fileName) || false;
    const isVideo = isVideoFile(file?.fileName) || false;

    if (isVideo && fileRef) {
      fileRef.current = { ...file, videoImgResourceId: file?.resourceId };
    }

    if (isAudio) {
      return;
    }

    if (isImage || isVideo) {
      setIsOpenMediaModal(true);
    } else {
      openFilePreviewModal(true);
    }
  };

  const chooseFile = () => {
    pickFile(file?.resourceId);
  };

  const showInfoFile = useMemo(() => {
    if (
      file?.actualContentType?.startsWith("video/") &&
      file?.videoImgResourceId
    ) {
      return <span>{formatTimeBySeconds(Math.round(file?.duration))}</span>;
    }

    return <span>{standardizeVolume(file?.volume)}</span>;
  }, [file]);

  const isPicked = useMemo(() => {
    return pickedList?.includes(file?.resourceId);
  }, [pickedList]);

  const progressRate = useMemo(() => {
    return (
      <div className="flex gap-[2px]">
        <div>
          {fileState?.progress
            ? standardizeVolume((fileState?.progress * file?.volume) / 100)
            : 0}
        </div>
        <div>/</div>
        <div>{standardizeVolume(file?.volume)}</div>
      </div>
    );
  }, [fileState?.progress]);

  useEffect(() => {
    setFileState(downloads[file?.resourceId]);
  }, [downloads]);

  return (
    <>
      <button className="flex flex-col">
        <div
          className={`w-[300px] p-3  transition cursor-pointer relative ${isPicked ? "bg-[#c2e7ff]" : "bg-[#f0f4f9] hover:bg-[#e1e3e1]"}`}
        >
          {/* ===== file name ===== */}
          <div className="flex items-center gap-2 mb-2 w-[70%]">
            <Checkbox
              onChange={(e) => {
                chooseFile();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              checked={isPicked}
            />
            <Tooltip title={file?.fileName}>
              <span className="font-medium text-sm truncate">
                {file?.fileName ?? "undefined"}
              </span>
            </Tooltip>
          </div>

          {/* ===== preview image ===== */}
          <button
            className="bg-gray-50 overflow-hidden h-[152px] w-full"
            onClick={handleOpenMediaModal}
          >
            {showPreview(file)}
          </button>

          {/* ===== user and time ===== */}
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            {/* <div>{file.owner}</div> */}
            <div>{formatTime(file?.createdAt)}</div>
            {showInfoFile}
          </div>

          <div className="absolute top-3 right-3 text-gray-500 hover:text-black">
            {renderDownloadButton()}
          </div>
        </div>
        {[
          DOWNLOAD_STATUS.DOWNLOADING,
          DOWNLOAD_STATUS.PAUSED,
          DOWNLOAD_STATUS.ERROR,
        ].includes(fileState?.status) ? (
          <div className="flex items-center gap-[2px]">
            <Progress
              className="flex-1"
              percent={fileState?.progress}
              showInfo={false}
              strokeColor={
                fileState?.status === DOWNLOAD_STATUS.ERROR
                  ? "#ff4d4f"
                  : "#52c41a"
              }
              size="small"
            />
            <div className="text-[grey] text-[10px]">{progressRate}</div>
          </div>
        ) : (
          <div className="h-[18px]" />
        )}
      </button>

      {isOpenMediaModal && fileRef && (
        <MediaStorageModal
          file={fileRef.current}
          onClose={() => setIsOpenMediaModal(false)}
        />
      )}

      {/* Render Modal Xem Office */}
      {isFilePreviewModal && (
        <PreviewOfficeModal
          isOpen={isFilePreviewModal}
          onCancel={() => setIsFilePreviewModal(false)}
          resourceId={resourceId}
          contextId={selectedConversation?.conversationId}
          pdfPreviewResourceId={file?.pdfPreviewResourceId}
          fileType={file?.mimeType || extensions}
          extensions={extensions}
        />
      )}
    </>
  );
};

export default MailStorageFile;

